import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getApiUrl } from '@/lib/subdomain'

const apiUrl = getApiUrl()

export function AdminCredentials() {
  const queryClient = useQueryClient()
  
  const { data: credentials, isLoading } = useQuery({
    queryKey: ['admin', 'credentials'],
    queryFn: async () => {
      const res = await fetch(`${apiUrl}/admin/credentials`, { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to fetch')
      return res.json()
    },
  })

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-display font-bold mb-2">API Credentials</h1>
        <p className="text-gray-400">Configure API keys to fetch stats from gaming platforms</p>
      </div>

      {/* Quick Status */}
      <div className="grid grid-cols-3 gap-4">
        <StatusBadge 
          platform="Steam" 
          configured={credentials?.steam?.configured}
          icon="🎮"
        />
        <StatusBadge 
          platform="Fortnite" 
          configured={credentials?.epic?.configured}
          icon="🎯"
        />
        <StatusBadge 
          platform="Xbox" 
          configured={credentials?.xbox?.configured}
          icon="🟢"
        />
      </div>

      <div className="grid gap-6">
        <SteamCredentialCard 
          credentials={credentials?.steam}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['admin', 'credentials'] })}
        />
        <EpicCredentialCard 
          credentials={credentials?.epic}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['admin', 'credentials'] })}
        />
        <XboxCredentialCard 
          credentials={credentials?.xbox}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['admin', 'credentials'] })}
        />
      </div>
    </div>
  )
}

function StatusBadge({ platform, configured, icon }: { platform: string; configured: boolean; icon: string }) {
  return (
    <div className={`p-4 rounded-xl border ${configured ? 'bg-green-500/10 border-green-500/30' : 'bg-gray-800/50 border-gray-700'}`}>
      <div className="flex items-center gap-3">
        <span className="text-2xl">{icon}</span>
        <div>
          <div className="font-medium">{platform}</div>
          <div className={`text-xs ${configured ? 'text-green-400' : 'text-gray-500'}`}>
            {configured ? '✓ Configured' : 'Not set up'}
          </div>
        </div>
      </div>
    </div>
  )
}

function CredentialCardWrapper({ 
  children, 
  platform, 
  icon, 
  configured,
  description
}: { 
  children: React.ReactNode
  platform: string
  icon: string
  configured: boolean
  description: string
}) {
  return (
    <div className="rounded-2xl border border-gray-800 overflow-hidden bg-gray-900/30">
      <div className="p-5 border-b border-gray-800 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-gray-800 flex items-center justify-center text-2xl">
          {icon}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">{platform}</h3>
            {configured ? (
              <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                Configured
              </span>
            ) : (
              <span className="text-xs bg-gray-700 text-gray-400 px-2 py-0.5 rounded-full">
                Not configured
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>
      <div className="p-5">
        {children}
      </div>
    </div>
  )
}

function SteamCredentialCard({ 
  credentials, 
  onUpdate 
}: { 
  credentials: any
  onUpdate: () => void 
}) {
  const [apiKey, setApiKey] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isEditing, setIsEditing] = useState(false)

  const saveMutation = useMutation({
    mutationFn: async (apiKey: string) => {
      const res = await fetch(`${apiUrl}/admin/credentials/steam`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ apiKey }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to save')
      return data
    },
    onSuccess: () => {
      setApiKey('')
      setError('')
      setSuccess('Steam API key saved!')
      setIsEditing(false)
      onUpdate()
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: Error) => setError(err.message),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiUrl}/admin/credentials/steam`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to delete')
    },
    onSuccess: onUpdate,
  })

  return (
    <CredentialCardWrapper
      platform="Steam API"
      icon="🎮"
      configured={credentials?.configured}
      description="Required to fetch Steam games, playtime, and achievements"
    >
      {/* Setup Instructions */}
      <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <h4 className="font-medium text-blue-400 mb-2">📋 How to get your Steam API Key</h4>
        <ol className="text-sm text-gray-400 space-y-1 list-decimal list-inside">
          <li>Go to <a href="https://steamcommunity.com/dev/apikey" target="_blank" rel="noopener" className="text-blue-400 hover:underline">steamcommunity.com/dev/apikey</a></li>
          <li>Log in with your Steam account</li>
          <li>Enter a domain name (can be anything, e.g., "n0n3.com")</li>
          <li>Copy the API key and paste it below</li>
        </ol>
      </div>

      {credentials?.configured && !isEditing ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg">
            <span className="text-gray-400">API Key:</span>
            <code className="text-green-400 font-mono">{credentials.apiKey}</code>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={() => setIsEditing(true)}
              className="btn bg-gray-700 hover:bg-gray-600"
            >
              ✏️ Update Key
            </button>
            <button 
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              className="btn bg-red-500/10 hover:bg-red-500/20 text-red-400"
            >
              🗑️ Remove
            </button>
            {success && <span className="text-green-400 flex items-center">{success}</span>}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Steam API Key</label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter your Steam API key"
              className="input w-full font-mono"
            />
          </div>
          
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
              {error}
            </div>
          )}
          
          <div className="flex gap-3">
            <button
              onClick={() => saveMutation.mutate(apiKey)}
              disabled={!apiKey || saveMutation.isPending}
              className="btn btn-primary"
            >
              {saveMutation.isPending ? 'Saving...' : 'Save API Key'}
            </button>
            {isEditing && (
              <button
                onClick={() => { setIsEditing(false); setApiKey(''); setError('') }}
                className="btn bg-gray-700 hover:bg-gray-600"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </CredentialCardWrapper>
  )
}

function EpicCredentialCard({ 
  credentials,
  onUpdate 
}: { 
  credentials: any
  onUpdate: () => void 
}) {
  const [apiKey, setApiKey] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isEditing, setIsEditing] = useState(false)

  const saveMutation = useMutation({
    mutationFn: async (apiKey: string) => {
      const res = await fetch(`${apiUrl}/admin/credentials/fortnite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ apiKey }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to save')
      return data
    },
    onSuccess: () => {
      setApiKey('')
      setError('')
      setSuccess('Fortnite API key saved!')
      setIsEditing(false)
      onUpdate()
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: Error) => setError(err.message),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiUrl}/admin/credentials/fortnite`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to delete')
    },
    onSuccess: onUpdate,
  })

  return (
    <CredentialCardWrapper
      platform="Fortnite API"
      icon="🎯"
      configured={credentials?.configured}
      description="Required for Fortnite stats (wins, kills, K/D, etc.)"
    >
      {/* Setup Instructions */}
      <div className="mb-4 p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
        <h4 className="font-medium text-purple-400 mb-2">📋 How to get your Fortnite API Key</h4>
        <ol className="text-sm text-gray-400 space-y-1 list-decimal list-inside">
          <li>Go to <a href="https://fortnite-api.com" target="_blank" rel="noopener" className="text-purple-400 hover:underline">fortnite-api.com</a></li>
          <li>Click "Get API Key" or sign up</li>
          <li>Copy your API key and paste it below</li>
        </ol>
        <p className="text-xs text-gray-500 mt-2">Free tier includes 3 requests/second - plenty for personal use!</p>
      </div>

      {credentials?.configured && !isEditing ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg">
            <span className="text-gray-400">API Key:</span>
            <code className="text-green-400 font-mono">{credentials.apiKey}</code>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={() => setIsEditing(true)}
              className="btn bg-gray-700 hover:bg-gray-600"
            >
              ✏️ Update Key
            </button>
            <button 
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              className="btn bg-red-500/10 hover:bg-red-500/20 text-red-400"
            >
              🗑️ Remove
            </button>
            {success && <span className="text-green-400 flex items-center">{success}</span>}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Fortnite API Key</label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="e.g., a1b2c3d4-e5f6-7890-abcd-ef1234567890"
              className="input w-full font-mono"
            />
          </div>
          
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
              {error}
            </div>
          )}
          
          <div className="flex gap-3">
            <button
              onClick={() => saveMutation.mutate(apiKey)}
              disabled={!apiKey || saveMutation.isPending}
              className="btn btn-primary"
            >
              {saveMutation.isPending ? 'Saving...' : 'Save API Key'}
            </button>
            {isEditing && (
              <button
                onClick={() => { setIsEditing(false); setApiKey(''); setError('') }}
                className="btn bg-gray-700 hover:bg-gray-600"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </CredentialCardWrapper>
  )
}

function XboxCredentialCard({ 
  credentials, 
  onUpdate 
}: { 
  credentials: any
  onUpdate: () => void 
}) {
  const [apiKey, setApiKey] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isEditing, setIsEditing] = useState(false)

  const saveMutation = useMutation({
    mutationFn: async (apiKey: string) => {
      const res = await fetch(`${apiUrl}/admin/credentials/xbox`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ apiKey }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to save')
      return data
    },
    onSuccess: () => {
      setApiKey('')
      setError('')
      setSuccess('Xbox API key saved!')
      setIsEditing(false)
      onUpdate()
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: Error) => setError(err.message),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiUrl}/admin/credentials/xbox`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to delete')
    },
    onSuccess: onUpdate,
  })

  return (
    <CredentialCardWrapper
      platform="Xbox / OpenXBL"
      icon="🟢"
      configured={credentials?.configured}
      description="Fetch Xbox gamerscore, achievements, and game history"
    >
      {/* Setup Instructions */}
      <div className="mb-4 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
        <h4 className="font-medium text-green-400 mb-2">📋 How to get your Xbox API Key (Easy Method)</h4>
        <p className="text-sm text-gray-400 mb-3">
          We recommend <strong>OpenXBL</strong> - it's much simpler than Microsoft Azure!
        </p>
        <ol className="text-sm text-gray-400 space-y-2 list-decimal list-inside">
          <li>
            Go to <a href="https://xbl.io" target="_blank" rel="noopener" className="text-green-400 hover:underline">xbl.io</a> (OpenXBL)
          </li>
          <li>Sign in with your Xbox/Microsoft account</li>
          <li>Go to "API Keys" in your dashboard</li>
          <li>Create a new API key (free tier: 150 requests/hour)</li>
          <li>Copy the API key and paste it below</li>
        </ol>
        
        <div className="mt-4 pt-4 border-t border-green-500/20">
          <details className="text-xs text-gray-500">
            <summary className="cursor-pointer hover:text-gray-400">
              Alternative: Microsoft Azure (Advanced)
            </summary>
            <div className="mt-2 pl-4">
              <p className="mb-2">For direct Xbox Live API access:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noopener" className="text-blue-400 hover:underline">portal.azure.com</a></li>
                <li>Create an "App Registration"</li>
                <li>Add Xbox Live API permissions</li>
                <li>Create a client secret</li>
              </ol>
              <p className="mt-2 text-yellow-500">⚠️ This method is complex and requires Azure setup. Use OpenXBL instead for simplicity.</p>
            </div>
          </details>
        </div>
      </div>

      {credentials?.configured && !isEditing ? (
        <XboxTestSection 
          credentials={credentials}
          onEdit={() => setIsEditing(true)}
          onDelete={() => deleteMutation.mutate()}
          deleting={deleteMutation.isPending}
          success={success}
        />
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">OpenXBL API Key</label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Your OpenXBL API key from xbl.io"
              className="input w-full font-mono"
            />
          </div>
          
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
              {error}
            </div>
          )}
          
          <div className="flex gap-3">
            <button
              onClick={() => saveMutation.mutate(apiKey)}
              disabled={!apiKey || saveMutation.isPending}
              className="btn btn-primary"
            >
              {saveMutation.isPending ? 'Saving...' : 'Save API Key'}
            </button>
            {isEditing && (
              <button
                onClick={() => { setIsEditing(false); setApiKey(''); setError('') }}
                className="btn bg-gray-700 hover:bg-gray-600"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </CredentialCardWrapper>
  )
}

// Xbox Test Section Component
function XboxTestSection({
  credentials,
  onEdit,
  onDelete,
  deleting,
  success,
}: {
  credentials: any
  onEdit: () => void
  onDelete: () => void
  deleting: boolean
  success: string
}) {
  const [testResult, setTestResult] = useState<any>(null)
  const [testing, setTesting] = useState(false)
  const [gamertag, setGamertag] = useState('')

  const testConnection = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const res = await fetch(`${apiUrl}/admin/credentials/xbox/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ gamertag: gamertag || undefined }),
      })
      const data = await res.json()
      setTestResult(data)
    } catch (err: any) {
      setTestResult({ error: err.message })
    } finally {
      setTesting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg">
        <span className="text-gray-400">API Key:</span>
        <code className="text-green-400 font-mono">{credentials.apiKey || credentials.clientId}</code>
        <span className="text-xs text-gray-500 ml-2">({credentials.type || 'openxbl'})</span>
      </div>
      
      {/* Test Connection */}
      <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
        <h4 className="font-medium text-gray-300 mb-3">Test Connection</h4>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={gamertag}
            onChange={(e) => setGamertag(e.target.value)}
            placeholder="Gamertag to test (e.g., n0n3za)"
            className="input flex-1 text-sm"
          />
          <button
            onClick={testConnection}
            disabled={testing}
            className="btn bg-green-500/20 hover:bg-green-500/30 text-green-400 text-sm"
          >
            {testing ? 'Testing...' : 'Test'}
          </button>
        </div>
        
        {testResult && (
          <div className={`p-3 rounded-lg text-sm ${testResult.success ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
            {testResult.success ? (
              <div className="space-y-1">
                <div>Connection successful!</div>
                <div>Gamertag: <strong>{testResult.gamertag}</strong></div>
                <div>Gamerscore: <strong>{testResult.gamerscore}</strong></div>
                <div className="text-xs text-gray-500">XUID: {testResult.xuid}</div>
              </div>
            ) : (
              <div>
                <div>{testResult.error}</div>
                {testResult.details && (
                  <pre className="mt-2 text-xs overflow-auto">{JSON.stringify(testResult.details, null, 2)}</pre>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="flex gap-3">
        <button 
          onClick={onEdit}
          className="btn bg-gray-700 hover:bg-gray-600"
        >
          Update Key
        </button>
        <button 
          onClick={onDelete}
          disabled={deleting}
          className="btn bg-red-500/10 hover:bg-red-500/20 text-red-400"
        >
          Remove
        </button>
        {success && <span className="text-green-400 flex items-center">{success}</span>}
      </div>
    </div>
  )
}
